import { getConfig } from "lib/config";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { CompanyApi } from "~/api/company-api";
import { SettingsApi } from "~/api/settings-api";
import { CustomFileInput } from "~/components/common/file-input";
import { Button } from "~/components/ui";
import useSettingState from "~/stores/setting";

export function CompanyLogoFormField({
  value,
  onChange,
  global = false,
}: {
  value: string | File;
  onChange: (val: File | string) => void;
  global?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settingState = useSettingState();
  const baseUrl = getConfig().BASE_URL;
  const translationKeyPrefix = global
    ? "globalSettings.fields.whiteLabelImage"
    : "company.settings.fields.whiteLabelImage";
  const [localValue, setLocalValue] = useState<string>(
    value instanceof File ? value.name : value,
  );
  const [src, setSrc] = useState<string>(
    value instanceof File ? "" : value ? `${baseUrl}/image/${value}` : "",
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileSrc, setImageFileSrc] = useState<string>("");

  useEffect(() => {
    if (!imageFile) {
      setImageFileSrc("");
      return;
    }

    const url = URL.createObjectURL(imageFile);
    setImageFileSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  function handleImageSelect(file: File) {
    setLocalValue(file.name);
    setImageFile(file);
    setSrc("");
    onChange(file);
  }

  async function handleImageDelete() {
    const result = global
      ? await new SettingsApi().deleteGlobalWhiteLabelImage()
      : await new CompanyApi().deleteWhiteLabelImage();
    if (!result.ok) {
      toast.error(t("common.errors.deleteFailed"));
      console.error(result.error);
      return;
    }

    toast.success(
      t("common.deleted", {
        resource: t(`${translationKeyPrefix}.label`),
      }),
    );
    setSrc("");
    setImageFile(null);
    setLocalValue("");
    onChange("");
    if (global) {
      settingState.setGlobalSettings({
        ...settingState.globalSettings,
        WHITE_LABEL_IMAGE: "",
      });
      queryClient.invalidateQueries({ queryKey: ["global:settings:all"] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["company:settings"] });
    }
  }

  return (
    <>
      <CustomFileInput
        value={localValue}
        placeholder={t(`${translationKeyPrefix}.placeholder`)}
        onChange={handleImageSelect}
        accept="image/*"
        className="w-fit dark:bg-input/30 border-input h-9 rounded-md border bg-background px-3 py-1 mb-1 text-base shadow-xs outline-none md:text-sm"
      />
      {imageFileSrc ? (
        <img src={imageFileSrc} alt="Company logo" />
      ) : src ? (
        <img src={src} alt="Company logo" />
      ) : (
        <></>
      )}
      <Button
        className="w-fit text-red-500 border-red-500"
        onClick={handleImageDelete}
        variant={"outline"}
        aria-label="Delete Image"
        disabled={localValue === ""}
        type="button"
      >
        {t("common.delete")}
      </Button>
    </>
  );
}
